import { useEffect, useState } from 'react';

export type OrientationMode = 'portrait' | 'landscape';

type OrientationWindow = Pick<Window, 'innerHeight' | 'innerWidth'> & {
    matchMedia?: Window['matchMedia'];
};

export function readOrientationMode(win?: OrientationWindow): OrientationMode {
    if (!win) return 'portrait';

    const media = win.matchMedia?.('(orientation: landscape)');
    if (media) return media.matches ? 'landscape' : 'portrait';

    return win.innerWidth > win.innerHeight ? 'landscape' : 'portrait';
}

export function useOrientationMode(): OrientationMode {
    const [mode, setMode] = useState<OrientationMode>(() => readOrientationMode(getWindow()));

    useEffect(() => {
        const win = getWindow();
        if (!win) return;

        const update = () => setMode(readOrientationMode(win));
        update();

        const media = win.matchMedia?.('(orientation: landscape)');
        if (media?.addEventListener) {
            media.addEventListener('change', update);
            return () => media.removeEventListener('change', update);
        }

        window.addEventListener('resize', update);
        return () => window.removeEventListener('resize', update);
    }, []);

    return mode;
}

function getWindow(): OrientationWindow | undefined {
    if (typeof window === 'undefined') return undefined;
    return window;
}
